import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { ArrowUpOnSquareIcon } from "@heroicons/react/24/outline";
import React, { useState, useEffect } from "react";

const BacktestResults = ({ results, botConfig }) => {
  const { results: allTrades, summary } = results;

  // State for all trades from props
  const [trades, setTrades] = useState(allTrades);
  // State for currently visible trades in the table
  const [visibleTrades, setVisibleTrades] = useState([]);

  const exchanges = { 1: "Binance", 2: "Bybit", 3: "Coinbase" };

  const selectedBot = botConfig?.["common-bot"] || 4;
  const selectedExchange = botConfig?.["common-exchange"] || "1";

  const handleExport = () => {
    if (!results?.file_base64 || !results?.filename) return;

    const link = document.createElement("a");
    link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${results?.file_base64}`;
    link.download = results?.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    // Update the main trades list
    setTrades(allTrades);
    // Initially, show only the first 10 trades
    setVisibleTrades(allTrades.slice(0, 10));
  }, [results, allTrades]);

  // Function to show all trades
  const handleShowMore = () => {
    setVisibleTrades(trades);
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);

  const formatCryptoQty = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 8,
      maximumFractionDigits: 8,
    }).format(v);

  const remainingTradesCount = trades.length - visibleTrades.length;

  return (
    <div className="mt-8 mb-8 p-5 bg-white rounded-2xl">
      {/* Header */}
      <div className="mb-5 flex justify-between items-center">
        <h3 className="font-bold text-left text-2xl">Performance Summary</h3>
        <div>
          <button
            className="w-fit bg-[#007A78] text-sm hover:bg-[#006a68] text-white font-semibold py-2.5 px-2.5 md:px-4 flex items-center gap-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors"
            onClick={handleExport}
          >
            <span className="hidden md:inline-block">Export Data</span>{" "}
            <ArrowUpOnSquareIcon className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="mb-8 bg-white">
        <div className="p-4 border border-[rgba(128,128,128,0.55)] rounded-[10px] bg-[#FEF7FF]">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                label: "Grid Profit",
                value: `${formatCurrency(
                  summary?.grid_profit || summary?.total_profit || 0
                )} USDT (${
                  summary?.grid_profit_pct || summary?.total_profit_pct || 0
                }%)`,
              },
              {
                label: "Total Fees",
                value: `${formatCurrency(summary?.fee_total || 0)} USDT`,
              },
              { label: "Total Trades", value: summary?.trade_count },
              {
                label: "Maximum Drawdown",
                value: `${summary?.max_drawdown_pct}%`,
              },
              { label: "Closed Buys", value: summary?.closed_buys },
              { label: "Open Buys", value: summary?.open_buys },
            ].map((stat, idx) => (
              <div key={idx} className="w-full flex flex-col">
                <span className="text-sm text-[#999] mb-1">{stat.label}</span>
                <span
                  className={`text-lg text-[#21272A] font-normal !m-0 ${
                    stat.positive
                      ? "text-green-600"
                      : stat.negative
                      ? "text-red-600"
                      : ""
                  }`}
                >
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trades Table */}
      <div className="bg-white">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-left text-2xl">Trade History</h3>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full text-sm border-collapse">
            <thead>
              <tr className="text-left bg-white">
                {[
                  "ID",
                  "Pair",
                  "Date & Time",
                  "Side",
                  "Type",
                  "Price",
                  "Amount",
                  "Total",
                  "Fee",
                  "PNL",
                ].map((h) => (
                  <th key={h} className="p-4 text-sm">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Map over visibleTrades instead of all trades */}
              {visibleTrades.map((t, i) => {
                const isBuy = t.side === "buy";
                const rowBg = isBuy
                  ? "bg-gradient-to-r from-[#D1E5CE] to-white"
                  : "bg-gradient-to-r from-[#F9DEDC] to-white";
                const rowOddBg = isBuy
                  ? "odd:bg-green-100/20"
                  : "odd:bg-red-100/20";

                return (
                  <tr key={i} className={`${rowBg} ${rowOddBg}`}>
                    <td className="p-4 text-[#21272A] text-sm">
                      #{t.position_id}
                    </td>
                    <td className="p-4 text-[#21272A] text-sm">{t.pair}</td>
                    <td className="p-4 text-[#21272A] text-sm">
                      {t?.created_at}
                    </td>
                    <td className="p-4 text-[#21272A] text-sm">
                      <span
                        className={`inline-block font-semibold capitalize rounded ${
                          isBuy ? "text-[#2D4E30]" : "text-[#B3261E]"
                        }`}
                      >
                        {t.side}
                      </span>
                    </td>
                    <td className="p-4 text-[#21272A] text-sm">
                      {t.order_type}
                    </td>
                    <td className="p-4 text-[#21272A] text-sm">
                      ${formatCurrency(t.price)}
                    </td>
                    <td className="p-4 text-[#21272A] text-sm">
                      {formatCryptoQty(t.qty)}
                    </td>
                    <td className="p-4 text-[#21272A] text-sm">
                      ${formatCurrency(t.usdt_qty)}
                    </td>
                    <td className="p-4 text-[#21272A] text-sm">
                      ${formatCryptoQty(t.fee)}
                    </td>
                    <td
                      className={`p-4 text-sm ${
                        t.pnl > 0
                          ? "text-green-600"
                          : t.pnl < 0
                          ? "text-red-600"
                          : "text-[#21272A]"
                      }`}
                    >
                      {t.pnl ? `$${formatCurrency(t.pnl)}` : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* "Show More" Button */}
        {remainingTradesCount > 0 && (
          <div className="mt-3 flex justify-center">
            <button
              onClick={handleShowMore}
              className="w-fit !bg-[transparent] flex items-center gap-2 hover:!bg-[transparent] !shadow-none text-[#4F378A] font-semibold py-2.5 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors"
            >
              See {remainingTradesCount} more results{" "}
              <ChevronDownIcon className="w-[20px] h-[20px]" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BacktestResults;
